import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Store, Goal, visionService } from '../../services/database/vision';
import { 
  BarChart3, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  Users,
  Briefcase,
  Activity,
  Target,
  Calendar,
  TrendingUp,
  Plus,
  X,
  CheckCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  FileText,
  Store as StoreIcon,
  Pencil
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Pie, Doughnut } from 'react-chartjs-2';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/modal';
import { PlanBadge } from '@/components/ui/plan-badge';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

// Interface do Modal de Metas
const GoalModal = ({ 
  isOpen, 
  onClose,
  onSave 
}: { 
  isOpen: boolean; 
  onClose: () => void;
  onSave: (goal: Omit<Goal, 'id' | 'current' | 'created_at' | 'updated_at'>) => void;
}) => {
  const [goal_type, setGoalType] = useState<GoalType>('delivered_stores');
  const [target, setTarget] = useState('');
  const [description, setDescription] = useState('');
  const currentDate = new Date();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!target || Number(target) <= 0) {
      toast.error('O valor da meta deve ser maior que zero');
      return;
    }

    onSave({
      goal_type,
      target: Number(target),
      description: description.trim() || undefined,
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear()
    });
    
    // Limpar o formulário
    setGoalType('delivered_stores');
    setTarget('');
    setDescription('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[rgb(28,28,30)] rounded-2xl border border-white/10 p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-[20px] font-semibold text-white">Nova Meta</h3>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-[rgb(44,44,46)] rounded-xl transition-all duration-200"
          >
            <X size={20} className="text-[rgb(152,152,157)]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[rgb(152,152,157)] text-sm mb-2">
              Tipo de Meta
            </label>
            <select
              value={goal_type}
              onChange={(e) => setGoalType(e.target.value as GoalType)}
              className="w-full bg-[rgb(44,44,46)] border border-white/10 rounded-xl px-4 py-2 text-white"
            >
              <option value="delivered_stores">Lojas Entregues</option>
              <option value="new_stores">Novas Lojas</option>
              <option value="office">Escritório</option>
            </select>
          </div>

          <div>
            <label className="block text-[rgb(152,152,157)] text-sm mb-2">
              Valor da Meta
            </label>
            <input
              type="number"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full bg-[rgb(44,44,46)] border border-white/10 rounded-xl px-4 py-2 text-white"
              placeholder="Digite o valor da meta"
              min="1"
              required
            />
          </div>

          {goal_type === 'office' && (
            <div>
              <label className="block text-[rgb(152,152,157)] text-sm mb-2">
                Descrição
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-[rgb(44,44,46)] border border-white/10 rounded-xl px-4 py-2 text-white resize-none"
                placeholder="Descreva a meta do escritório"
                rows={3}
              />
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200"
          >
            Salvar Meta
          </button>
        </form>
      </div>
    </div>
  );
};

export const VisionDashboard = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return {
      month: now.getMonth() + 1,
      year: now.getFullYear()
    };
  });
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [newGoal, setNewGoal] = useState({
    goal_type: 'delivered_stores' as GoalType,
    target: 0,
    description: '',
    month: selectedDate.month,
    year: selectedDate.year
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'monthly'>('monthly');
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

  useEffect(() => {
    loadStores();
    loadGoals();
  }, [selectedDate, viewMode]);

  const loadStores = async () => {
    try {
      let data;
      if (viewMode === 'monthly') {
        data = await visionService.getStoresByMonth(selectedDate.month, selectedDate.year);
      } else {
        data = await visionService.getStores();
      }
      setStores(data);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
      toast.error('Erro ao carregar lojas');
    } finally {
      setLoading(false);
    }
  };

  const loadGoals = async () => {
    try {
      const data = await visionService.getGoals(selectedDate.month, selectedDate.year);
      setGoals(data);
    } catch (error) {
      console.error('Erro ao carregar metas:', error);
      toast.error('Erro ao carregar metas');
    }
  };

  const handleSaveGoal = async (goal: Omit<Goal, 'id' | 'current' | 'created_at' | 'updated_at'>) => {
    if (!goal.goal_type || !goal.target || !goal.month || !goal.year) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    try {
      await visionService.upsertGoal(goal);
      toast.success('Meta salva com sucesso!');
      loadGoals();
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
      toast.error('Erro ao salvar meta');
    }
  };

  const handleGoalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await handleSaveGoal(newGoal);
      setNewGoal({
        goal_type: 'delivered_stores' as GoalType,
        target: 0,
        description: '',
        month: selectedDate.month,
        year: selectedDate.year
      });
      setShowGoalModal(false);
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProgress = async (goal: Goal, newCurrent: number, showToast = false) => {
    try {
      await visionService.updateGoalProgress(goal.id, newCurrent);
      if (showToast) {
        toast.success('Progresso atualizado com sucesso!');
      }
      loadGoals();
      setEditingGoal(null);
    } catch (error) {
      console.error('Erro ao atualizar progresso:', error);
      toast.error('Erro ao atualizar progresso');
    }
  };

  const handleUpdateGoal = async (goal: Goal, updates: { target?: number; description?: string }) => {
    try {
      await visionService.updateGoal(goal.id, updates);
      toast.success('Meta atualizada com sucesso!');
      loadGoals();
      setEditingGoal(null);
    } catch (error) {
      console.error('Erro ao atualizar meta:', error);
      toast.error('Erro ao atualizar meta');
    }
  };

  // Cálculos básicos
  const metrics = useMemo(() => {
    const totalStores = stores.length;
    const completedStores = stores.filter(store => store.completed_at).length;
    const inProgressStores = stores.filter(store => !store.completed_at).length;
    const overdueTasks = stores.filter(store => {
      if (store.completed_at) return false;
      const deadline = new Date(store.deadline);
      return new Date() > deadline;
    }).length;

    // KPIs e Metas
    const monthlyGoal = 20; // Meta mensal de entregas
    const currentMonthCompleted = stores.filter(store => {
      const completedAt = store.completed_at;
      if (!completedAt) return false;
      const date = new Date(completedAt);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length;

    const goalProgress = (currentMonthCompleted / monthlyGoal) * 100;

    return {
      totalStores,
      completedStores,
      inProgressStores,
      overdueTasks,
      monthlyGoal,
      currentMonthCompleted,
      goalProgress
    };
  }, [stores]);

  // Dados para o gráfico de linha
  const deliveriesData = useMemo(() => {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const deliveriesByMonth = new Array(12).fill(0);
    
    stores.forEach(store => {
      if (!store.completed_at) return;
      const date = new Date(store.completed_at);
      deliveriesByMonth[date.getMonth()]++;
    });

    return {
      labels: months,
      datasets: [
        {
          label: 'Entregas',
          data: deliveriesByMonth,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  }, [stores]);

  const deliveriesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        displayColors: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.05)'
        },
        border: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          padding: 8,
          font: {
            size: 13
          }
        }
      },
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        border: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          padding: 8,
          font: {
            size: 13
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  // Dados para o gráfico de status
  const statusData = useMemo(() => {
    const statusCount = stores.reduce((acc, store) => {
      Object.values(store.status).forEach(status => {
        acc[status] = (acc[status] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);

    return {
      labels: ['Concluídas', 'Em Andamento', 'Não Iniciadas'],
      datasets: [
        {
          data: [
            statusCount.done || 0,
            statusCount.in_progress || 0,
            statusCount.not_started || 0
          ],
          backgroundColor: [
            'rgba(16, 185, 129, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(107, 114, 128, 0.8)'
          ],
          borderColor: [
            'rgb(16, 185, 129)',
            'rgb(245, 158, 11)',
            'rgb(107, 114, 128)'
          ],
          borderWidth: 2
        }
      ]
    };
  }, [stores]);

  const statusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          padding: 16,
          font: {
            size: 13
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1
      }
    },
    cutout: '60%'
  };

  // Próximas entregas
  const upcomingDeliveries = useMemo(() => {
    return stores
      .filter(store => !store.completed_at)
      .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime())
      .slice(0, 5);
  }, [stores]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setViewMode('monthly'); // Volta para visualização mensal ao navegar
    setSelectedDate(prev => {
      const newDate = new Date(prev.year, prev.month - 1);
      if (direction === 'next') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else {
        newDate.setMonth(newDate.getMonth() - 1);
      }
      return {
        month: newDate.getMonth() + 1,
        year: newDate.getFullYear()
      };
    });
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'monthly' ? 'all' : 'monthly');
  };

  const formatMonthYear = (month: number, year: number) => {
    return new Date(year, month - 1).toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric'
    });
  };

  // Calcular o total de lojas do mês atual
  const currentMonthStores = useMemo(() => stores.filter(store => {
    const storeDate = new Date(store.created_at);
    return storeDate.getMonth() === selectedDate.month - 1 && storeDate.getFullYear() === selectedDate.year;
  }), [stores, selectedDate.month, selectedDate.year]);

  // Calcular o total de lojas concluídas no mês atual
  const completedStores = useMemo(() => stores.filter(store => {
    if (!store.completed_at) return false;
    const completedDate = new Date(store.completed_at);
    return completedDate.getMonth() === selectedDate.month - 1 && completedDate.getFullYear() === selectedDate.year;
  }), [stores, selectedDate.month, selectedDate.year]);

  useEffect(() => {
    const updateGoals = async () => {
      const newStoresGoal = goals.find(g => g.goal_type === 'new_stores');
      const deliveredStoresGoal = goals.find(g => g.goal_type === 'delivered_stores');

      const updates = [];

      if (newStoresGoal && currentMonthStores.length !== newStoresGoal.current) {
        updates.push(visionService.updateGoalProgress(newStoresGoal.id, currentMonthStores.length));
      }
      if (deliveredStoresGoal && completedStores.length !== deliveredStoresGoal.current) {
        updates.push(visionService.updateGoalProgress(deliveredStoresGoal.id, completedStores.length));
      }

      if (updates.length > 0) {
        try {
          await Promise.all(updates);
          loadGoals();
        } catch (error) {
          console.error('Erro ao atualizar metas:', error);
        }
      }
    };

    updateGoals();
  }, [stores]); // Atualizar apenas quando as lojas mudarem

  const renderProgressEdit = (goal: Goal | null) => {
    if (!goal) return null;

    const isOfficeGoal = goal.goal_type === 'office';

    return (
      <div className="absolute inset-0 bg-[rgb(28,28,30)]/95 backdrop-blur-md rounded-2xl flex items-center justify-center p-6 border border-white/10">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <h3 className="text-[20px] font-semibold text-white">
              {isOfficeGoal ? 'Editar Meta do Escritório' : 'Editar Meta'}
            </h3>
            <p className="text-[rgb(152,152,157)] text-sm">
              {isOfficeGoal 
                ? 'Defina a descrição da meta do escritório para este mês' 
                : 'Defina o número total de lojas como meta para este mês'}
            </p>
          </div>

          {isOfficeGoal ? (
            <div className="space-y-2">
              <label className="block text-[rgb(152,152,157)] text-sm">
                Descrição da Meta
              </label>
              <input
                type="text"
                defaultValue={goal.description || ''}
                placeholder="Ex: Implementar novo sistema"
                className="w-full bg-[rgb(44,44,46)] border border-white/10 rounded-xl px-4 py-3 text-[17px] text-white placeholder:text-[rgb(152,152,157)] focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const input = e.target as HTMLInputElement;
                    handleUpdateGoal(goal, { description: input.value });
                  }
                }}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-[rgb(152,152,157)] text-sm">
                Meta Total
              </label>
              <input
                type="number"
                defaultValue={goal.target}
                min="1"
                className="w-full bg-[rgb(44,44,46)] border border-white/10 rounded-xl px-4 py-3 text-[17px] text-white placeholder:text-[rgb(152,152,157)] focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const input = e.target as HTMLInputElement;
                    handleUpdateGoal(goal, { target: Number(input.value) });
                  }
                }}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={() => setEditingGoal(null)}
              className="flex-1 bg-[rgb(44,44,46)] text-white rounded-2xl py-3 px-4 text-sm font-medium hover:bg-[rgb(28,28,30)] transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              onClick={(e) => {
                const input = e.currentTarget.parentElement?.parentElement?.querySelector('input');
                if (input) {
                  if (isOfficeGoal) {
                    handleUpdateGoal(goal, { description: input.value });
                  } else {
                    const value = Number(input.value);
                    if (value < 1) {
                      toast.error('A meta deve ser maior que zero');
                      return;
                    }
                    handleUpdateGoal(goal, { target: value });
                  }
                }
              }}
              className="flex-1 bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderGoalCard = (type: GoalType, title: string, icon: React.ReactNode) => {
    const goal = goals.find(g => g.goal_type === type);
    const progress = goal ? Math.round((goal.current / goal.target) * 100) : 0;
    const isOfficeGoal = type === 'office';

    return (
      <div className="relative bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 group hover:bg-[rgb(44,44,46)]/60 transition-all duration-200">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 rounded-xl bg-[#00B7E2]/10">
            {icon}
          </div>
          <h3 className="text-[20px] font-semibold text-white">{title}</h3>
        </div>

        {goal ? (
          <div className="space-y-6">
            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-center gap-3">
                  {isOfficeGoal ? (
                    <p className="text-[17px] text-white">
                      {goal.description || 'Sem descrição'}
                    </p>
                  ) : (
                    <p className="text-[32px] font-semibold text-white tracking-tight">
                      {goal.current}
                      <span className="text-[rgb(152,152,157)] text-[17px] ml-2">
                        / {goal.target}
                      </span>
                    </p>
                  )}
                  <button 
                    onClick={() => setEditingGoal(goal)}
                    className="opacity-0 group-hover:opacity-100 p-2 hover:bg-[rgb(44,44,46)] rounded-xl transition-all duration-200"
                  >
                    <Pencil className="w-4 h-4 text-[rgb(152,152,157)]" />
                  </button>
                </div>
                <p className="text-[rgb(152,152,157)] text-sm mt-1">
                  {type === 'new_stores' && `${currentMonthStores.length} lojas iniciadas este mês`}
                  {type === 'delivered_stores' && `${completedStores.length} lojas finalizadas este mês`}
                  {type === 'office' && 'Clique no lápis para editar a meta'}
                </p>
              </div>
              {!isOfficeGoal && (
                <div className="text-right">
                  <p className="text-[17px] font-medium text-[#00B7E2]">
                    {progress}%
                  </p>
                  <p className="text-[rgb(152,152,157)] text-sm">completo</p>
                </div>
              )}
            </div>
            
            {!isOfficeGoal && (
              <div className="h-2 bg-[rgb(44,44,46)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#00B7E2] rounded-full transition-all duration-500 ease-in-out"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-[rgb(152,152,157)] text-sm mb-4">
              Nenhuma meta definida para {title.toLowerCase()} este mês
            </p>
            <button
              onClick={() => setShowGoalModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[rgb(44,44,46)] text-white rounded-2xl hover:bg-[rgb(28,28,30)] transition-all duration-200 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Definir Meta
            </button>
          </div>
        )}

        {editingGoal?.id === goal?.id && renderProgressEdit(editingGoal)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Background com gradiente */}
      <div className="fixed left-0 right-0 top-0 h-[500px] bg-gradient-to-b from-[#00B7E2]/20 via-[#00B7E2]/5 to-transparent pointer-events-none" />

      {/* Conteúdo */}
      <div className="relative space-y-6">
        {/* Cabeçalho com Navegação */}
        <div className="relative space-y-4">
          {/* Navegador de Datas */}
          <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
            <div className="flex justify-end items-center">
              <div className="flex items-center gap-4">
                {/* Navegação de Meses */}
                <div className="flex items-center gap-2 bg-[rgb(44,44,46)] rounded-2xl p-1">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 hover:bg-[rgb(28,28,30)] rounded-xl transition-all duration-200"
                    disabled={viewMode === 'all'}
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  
                  <span className="text-[17px] font-medium text-white px-3">
                    {viewMode === 'monthly' 
                      ? formatMonthYear(selectedDate.month, selectedDate.year)
                      : 'Todos os Meses'
                    }
                  </span>
                  
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 hover:bg-[rgb(28,28,30)] rounded-xl transition-all duration-200"
                    disabled={viewMode === 'all'}
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Botão Ver Tudo / Ver Mês */}
                <button
                  onClick={toggleViewMode}
                  className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-200 ${
                    viewMode === 'all'
                      ? 'bg-white text-black hover:bg-white/90'
                      : 'bg-[rgb(44,44,46)] text-white hover:bg-[rgb(28,28,30)]'
                  }`}
                >
                  {viewMode === 'all' ? (
                    <>
                      <Calendar className="w-4 h-4" />
                      Ver por Mês
                    </>
                  ) : (
                    <>
                      <LayoutGrid className="w-4 h-4" />
                      Ver Tudo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total de Lojas - Card com destaque */}
            <div className="bg-gradient-to-br from-[#00B7E2]/10 via-[#00B7E2]/5 to-[#00B7E2]/10 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[#00B7E2]/20 transition-all duration-200 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#00B7E2]/20 rounded-xl">
                  <Briefcase className="w-6 h-6 text-[#00B7E2]" />
                </div>
                <div>
                  <h3 className="text-[rgb(152,152,157)] text-sm">Total de Lojas</h3>
                  <p className="text-[20px] font-semibold text-white mt-1">{metrics.totalStores}</p>
                </div>
              </div>
            </div>

            {/* Em Produção */}
            <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/20 rounded-xl">
                  <Activity className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-[rgb(152,152,157)] text-sm">Em Produção</h3>
                  <p className="text-[20px] font-semibold text-white mt-1">{metrics.inProgressStores}</p>
                </div>
              </div>
            </div>

            {/* Concluídas */}
            <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-[rgb(152,152,157)] text-sm">Concluídas</h3>
                  <p className="text-[20px] font-semibold text-white mt-1">{metrics.completedStores}</p>
                </div>
              </div>
            </div>

            {/* Atrasadas */}
            <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-[rgb(152,152,157)] text-sm">Atrasadas</h3>
                  <p className="text-[20px] font-semibold text-white mt-1">{metrics.overdueTasks}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Gráfico de Entregas */}
            <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[20px] font-semibold text-white">Entregas por Mês</h3>
                <div className="p-3 bg-[#00B7E2]/10 rounded-xl">
                  <BarChart3 className="w-6 h-6 text-[#00B7E2]" />
                </div>
              </div>
              <div className="h-[300px]">
                <Line data={{
                  ...deliveriesData,
                  datasets: [{
                    ...deliveriesData.datasets[0],
                    borderColor: '#00B7E2',
                    backgroundColor: 'rgba(0, 183, 226, 0.1)',
                    fill: true,
                    tension: 0.4,
                    borderWidth: 3,
                    pointRadius: 0,
                    pointHoverRadius: 6,
                    pointHoverBackgroundColor: '#00B7E2',
                    pointHoverBorderColor: '#fff',
                    pointHoverBorderWidth: 2
                  }]
                }} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      backgroundColor: 'rgb(28,28,30)',
                      titleColor: 'rgb(255,255,255)',
                      bodyColor: 'rgb(152,152,157)',
                      borderColor: 'rgb(44,44,46)',
                      borderWidth: 1,
                      padding: 12,
                      titleFont: {
                        size: 14,
                        weight: 'bold'
                      },
                      bodyFont: {
                        size: 13
                      },
                      displayColors: false,
                      intersect: false,
                      mode: 'index'
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(255,255,255,0.03)',
                        drawBorder: false
                      },
                      border: {
                        display: false
                      },
                      ticks: {
                        color: 'rgb(152,152,157)',
                        padding: 8,
                        font: {
                          size: 13
                        }
                      }
                    },
                    x: {
                      grid: {
                        display: false,
                        drawBorder: false
                      },
                      border: {
                        display: false
                      },
                      ticks: {
                        color: 'rgb(152,152,157)',
                        padding: 8,
                        font: {
                          size: 13
                        }
                      }
                    }
                  },
                  interaction: {
                    intersect: false,
                    mode: 'index'
                  }
                }} />
              </div>
            </div>

            {/* Gráfico de Status */}
            <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[20px] font-semibold text-white">Distribuição de Status</h3>
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <Activity className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div className="h-[300px]">
                <Doughnut data={{
                  ...statusData,
                  datasets: [{
                    ...statusData.datasets[0],
                    backgroundColor: [
                      'rgba(16, 185, 129, 0.2)',
                      'rgba(245, 158, 11, 0.2)',
                      'rgba(107, 114, 128, 0.2)'
                    ],
                    borderColor: [
                      'rgb(16, 185, 129)',
                      'rgb(245, 158, 11)',
                      'rgb(107, 114, 128)'
                    ],
                    borderWidth: 2
                  }]
                }} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        color: 'rgb(152,152,157)',
                        padding: 16,
                        font: {
                          size: 13
                        },
                        boxWidth: 16,
                        boxHeight: 16
                      }
                    },
                    tooltip: {
                      backgroundColor: 'rgb(28,28,30)',
                      titleColor: 'rgb(255,255,255)',
                      bodyColor: 'rgb(152,152,157)',
                      borderColor: 'rgb(44,44,46)',
                      borderWidth: 1,
                      padding: 12,
                      titleFont: {
                        size: 14,
                        weight: 'bold'
                      },
                      bodyFont: {
                        size: 13
                      }
                    }
                  },
                  cutout: '70%'
                }} />
              </div>
            </div>
          </div>

          {/* Metas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderGoalCard(
              'new_stores',
              'Novas Lojas',
              <StoreIcon className="w-6 h-6 text-[#00B7E2]" />,
            )}
            {renderGoalCard(
              'delivered_stores',
              'Lojas Entregues',
              <CheckCircle className="w-6 h-6 text-[#00B7E2]" />,
            )}
            {renderGoalCard(
              'office',
              'Escritório',
              <Building2 className="w-6 h-6 text-[#00B7E2]" />,
            )}
          </div>

          {/* Próximas Entregas */}
          <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 p-6 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[20px] font-semibold text-white">Próximas Entregas</h3>
              <div className="p-3 bg-blue-500/10 rounded-xl">
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <div className="space-y-3">
              {upcomingDeliveries.map(store => {
                const daysLeft = Math.ceil(
                  (new Date(store.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                );
                const isOverdue = daysLeft < 0;

                return (
                  <div 
                    key={store.id}
                    className="flex items-center justify-between p-4 bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200 shadow-lg"
                  >
                    <div>
                      <h4 className="text-[17px] font-medium text-white">{store.name}</h4>
                      <p className="text-sm text-[rgb(152,152,157)] mt-1">
                        {Object.values(store.status).filter(s => s === 'done').length} de 4 etapas concluídas
                      </p>
                    </div>
                    <span className={`px-4 py-2 rounded-2xl text-sm font-medium ${
                      isOverdue 
                        ? 'bg-red-500/10 text-red-500' 
                        : daysLeft <= 3
                        ? 'bg-yellow-500/10 text-yellow-500'
                        : 'bg-green-500/10 text-green-500'
                    }`}>
                      {isOverdue 
                        ? `${Math.abs(daysLeft)} dias atrasado` 
                        : daysLeft === 0
                        ? 'Entrega hoje'
                        : `${daysLeft} dias restantes`
                      }
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Modal de Metas */}
          <div
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 ${
              showGoalModal ? 'block' : 'hidden'
            }`}
          >
            <div className="bg-[#111] rounded-xl border border-white/[0.08] p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Nova Meta</h3>
                <button onClick={() => setShowGoalModal(false)} className="p-2 hover:bg-white/[0.08] rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleGoalSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Tipo de Meta</label>
                  <select
                    value={newGoal.goal_type}
                    onChange={(e) => setNewGoal({ ...newGoal, goal_type: e.target.value as GoalType })}
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 focus:border-blue-500 transition-all"
                    required
                  >
                    <option value="">Selecione um tipo</option>
                    <option value="delivered_stores">Lojas Entregues</option>
                    <option value="new_stores">Novas Lojas</option>
                    <option value="office">Meta do Escritório</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium">Meta</label>
                  <input
                    type="number"
                    value={newGoal.target}
                    onChange={(e) => setNewGoal({ ...newGoal, target: parseInt(e.target.value) })}
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 focus:border-blue-500 transition-all"
                    required
                    min="1"
                  />
                </div>

                {newGoal.goal_type === 'office' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Descrição</label>
                    <textarea
                      value={newGoal.description}
                      onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 focus:border-blue-500 transition-all"
                      rows={3}
                      required
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Mês</label>
                    <select
                      value={newGoal.month || selectedDate.month}
                      onChange={(e) => setNewGoal({ ...newGoal, month: parseInt(e.target.value) })}
                      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 focus:border-blue-500 transition-all"
                      required
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2024, i).toLocaleDateString('pt-BR', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Ano</label>
                    <select
                      value={newGoal.year || selectedDate.year}
                      onChange={(e) => setNewGoal({ ...newGoal, year: parseInt(e.target.value) })}
                      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 focus:border-blue-500 transition-all"
                      required
                    >
                      {Array.from({ length: 3 }, (_, i) => (
                        <option key={i} value={new Date().getFullYear() + i}>
                          {new Date().getFullYear() + i}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowGoalModal(false)}
                    className="px-4 py-2 text-white/60 hover:text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
